import { getAuth } from 'firebase/auth';
import type {
  BillingAccessSnapshot,
  BillingAccount,
  BillingApiResponse,
  BillingCreditLedgerEntry,
  BillingProduct,
  BillingProjectSummary,
  BillingSubscription,
  BillingUsageMeter,
  CreditPackCheckoutSession,
  CreateManualBillingSubscriptionInput,
  ImpressionWalletResponse,
  UpdateBillingSubscriptionInput,
} from '../types';
import { parseBillingBlockedPayload } from './BillingBlockedError';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  const text = await response.text().catch(() => '');
  let payload: BillingApiResponse<T> | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from billing API: HTTP ${response.status}`);
    }
  }
  if (!response.ok || payload?.success === false) {
    const blocked = parseBillingBlockedPayload(response.status, payload as unknown as Record<string, unknown> | undefined);
    if (blocked) throw blocked;
    throw new Error(payload?.error || `HTTP ${response.status}`);
  }
  return payload?.data as T;
}

export class BillingAccessApiService {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  async listProducts(): Promise<BillingProduct[]> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/products`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingProduct[]>(response);
  }

  async createProduct(input: Partial<BillingProduct>): Promise<BillingProduct> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/products`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse<BillingProduct>(response);
  }

  async updateProduct(productId: string, input: Partial<BillingProduct>): Promise<BillingProduct> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/products/${encodeURIComponent(productId)}`, {
      method: 'PATCH',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse<BillingProduct>(response);
  }

  async archiveProduct(productId: string): Promise<BillingProduct> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/products/${encodeURIComponent(productId)}`, {
      method: 'DELETE',
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingProduct>(response);
  }

  async listMeters(): Promise<BillingUsageMeter[]> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/meters`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingUsageMeter[]>(response);
  }

  async listProjects(query?: string): Promise<BillingProjectSummary[]> {
    const params = new URLSearchParams();
    params.set('limit', 'all');
    if (query?.trim()) {
      params.set('q', query.trim());
    }
    const suffix = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(`${this.baseUrl}/api/billing/admin/projects${suffix}`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingProjectSummary[]>(response);
  }

  async listProjectSubscriptions(projectId: string): Promise<BillingSubscription[]> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/admin/projects/${encodeURIComponent(projectId)}/subscriptions`,
      {
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<BillingSubscription[]>(response);
  }

  async createManualSubscription(
    projectId: string,
    input: CreateManualBillingSubscriptionInput,
  ): Promise<BillingSubscription> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/admin/projects/${encodeURIComponent(projectId)}/subscriptions/manual`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse<BillingSubscription>(response);
  }

  async updateProjectSubscription(
    projectId: string,
    subscriptionId: string,
    input: UpdateBillingSubscriptionInput,
  ): Promise<BillingSubscription> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/admin/projects/${encodeURIComponent(projectId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
      {
        method: 'PATCH',
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse<BillingSubscription>(response);
  }

  async deleteProjectSubscription(projectId: string, subscriptionId: string): Promise<{ id: string }> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/admin/projects/${encodeURIComponent(projectId)}/subscriptions/${encodeURIComponent(subscriptionId)}`,
      {
        method: 'DELETE',
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<{ id: string }>(response);
  }

  async createMeter(input: Partial<BillingUsageMeter>): Promise<BillingUsageMeter> {
    const response = await fetch(`${this.baseUrl}/api/billing/catalog/meters`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse<BillingUsageMeter>(response);
  }

  async getAccount(projectId: string): Promise<BillingAccount> {
    const response = await fetch(`${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/account`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingAccount>(response);
  }

  async getAccessSnapshot(projectId: string): Promise<BillingAccessSnapshot> {
    const response = await fetch(`${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/access`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingAccessSnapshot>(response);
  }

  async recomputeAccessSnapshot(projectId: string): Promise<BillingAccessSnapshot> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/access/recompute`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<BillingAccessSnapshot>(response);
  }

  async listCreditPacks(projectId: string): Promise<BillingProduct[]> {
    const response = await fetch(`${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/credit-packs`, {
      headers: await getAuthHeaders(),
    });
    return handleResponse<BillingProduct[]>(response);
  }

  async createCreditPackCheckoutSession(
    projectId: string,
    productId: string,
    input: { successUrl?: string; cancelUrl?: string; mode?: 'test' | 'live' } = {},
  ): Promise<CreditPackCheckoutSession> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/credit-packs/${encodeURIComponent(productId)}/checkout-session`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse<CreditPackCheckoutSession>(response);
  }

  /**
   * Start a self-serve platform subscription checkout (the hard-paywall Subscribe
   * CTA). Returns the same `{ id, url }` shape as credit-pack checkout.
   */
  async createSubscriptionCheckoutSession(
    projectId: string,
    productId?: string,
    input: { successUrl?: string; cancelUrl?: string; mode?: 'test' | 'live' } = {},
  ): Promise<CreditPackCheckoutSession> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/subscriptions/checkout-session`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ ...input, ...(productId ? { productId } : {}) }),
      },
    );
    return handleResponse<CreditPackCheckoutSession>(response);
  }

  async listSubscriptionProducts(projectId: string): Promise<BillingProduct[]> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/subscription-products`,
      { headers: await getAuthHeaders() },
    );
    return handleResponse<BillingProduct[]>(response);
  }

  /** Active subscription products whose capabilities grant `capability` (names the product on a paywall). */
  async listCapabilityProducts(projectId: string, capability: string): Promise<BillingProduct[]> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/capability-products?capability=${encodeURIComponent(capability)}`,
      { headers: await getAuthHeaders() },
    );
    return handleResponse<BillingProduct[]>(response);
  }

  async cancelSubscription(projectId: string, subscriptionId: string): Promise<BillingSubscription> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/subscriptions/${encodeURIComponent(subscriptionId)}/cancel`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<BillingSubscription>(response);
  }

  async reactivateSubscription(projectId: string, subscriptionId: string): Promise<BillingSubscription> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/subscriptions/${encodeURIComponent(subscriptionId)}/reactivate`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<BillingSubscription>(response);
  }

  async listImpressionPacks(projectId: string): Promise<BillingProduct[]> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/impression-packs`,
      { headers: await getAuthHeaders() },
    );
    return handleResponse<BillingProduct[]>(response);
  }

  async getImpressionWallet(projectId: string): Promise<ImpressionWalletResponse> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/impression-wallet`,
      { headers: await getAuthHeaders() },
    );
    return handleResponse<ImpressionWalletResponse>(response);
  }

  async createImpressionPackCheckoutSession(
    projectId: string,
    productId: string,
    input: { successUrl?: string; cancelUrl?: string; mode?: 'test' | 'live' } = {},
  ): Promise<CreditPackCheckoutSession> {
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/impression-packs/${encodeURIComponent(productId)}/checkout-session`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(input),
      },
    );
    return handleResponse<CreditPackCheckoutSession>(response);
  }

  async listCreditLedger(projectId: string, limit = 25): Promise<BillingCreditLedgerEntry[]> {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    const response = await fetch(
      `${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/credits/ledger?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
      },
    );
    return handleResponse<BillingCreditLedgerEntry[]>(response);
  }

  async grantProjectCredits(
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ): Promise<BillingCreditLedgerEntry> {
    const response = await fetch(`${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/credits/grant`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse<BillingCreditLedgerEntry>(response);
  }

  async debitProjectCredits(
    projectId: string,
    input: { amountMinor: number; source?: string; sourceId?: string },
  ): Promise<BillingCreditLedgerEntry> {
    const response = await fetch(`${this.baseUrl}/api/billing/projects/${encodeURIComponent(projectId)}/credits/debit`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify(input),
    });
    return handleResponse<BillingCreditLedgerEntry>(response);
  }
}

export function createBillingAccessApiService(baseUrl?: string): BillingAccessApiService {
  return new BillingAccessApiService(baseUrl);
}
